<!-- components/form/FieldControl.vue -->
<script setup lang="ts">
import { computed, inject, onBeforeUnmount, watch } from 'vue'
import { useField } from 'vee-validate'

type Density = 'compact' | 'comfortable' | 'default'
type ValidateOn = 'input' | 'blur' | 'submit'

interface FormCtx {
  disabled: { value: boolean }
  readonly: { value: boolean }
  density: { value: Density }
  validateOn: { value: ValidateOn }
}
const FORM_CTX = Symbol.for('FormCtx')

const props = withDefaults(
  defineProps<{
    name: string
    modelValue?: unknown
    normalize?: (v: unknown) => unknown
    mask?: (v: unknown) => unknown
    validateOn?: ValidateOn
    disabled?: boolean
    readonly?: boolean
    debounce?: number
    rules?: unknown
  }>(),
  {
    modelValue: undefined,
    normalize: undefined,
    mask: undefined,
    validateOn: undefined,
    disabled: false,
    readonly: false,
    debounce: 0,
    rules: undefined,
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', v: unknown): void
  (e: 'focus'): void
  (e: 'blur'): void
  (e: 'enter'): void
  (e: 'clear'): void
  (e: 'change', v: unknown): void
}>()

const formCtx = inject<FormCtx | null>(FORM_CTX, null)

const resolvedValidateOn = computed<ValidateOn>(
  () => props.validateOn ?? formCtx?.validateOn?.value ?? 'blur'
)
const resolvedDisabled = computed<boolean>(
  () => props.disabled ?? formCtx?.disabled?.value ?? false
)
const resolvedReadonly = computed<boolean>(
  () => props.readonly ?? formCtx?.readonly?.value ?? false
)

/** Tipamos o field como unknown p/ evitar any */
const {
  value,
  errorMessage,
  meta,
  setTouched,
  handleChange,
  setValue,
  resetField,
} = useField<unknown>(props.name, props.rules as never, {
  initialValue: props.modelValue as unknown,
  validateOnValueUpdate: resolvedValidateOn.value === 'input',
})

watch(
  () => props.modelValue,
  (ext) => {
    if (ext !== value.value) setValue(ext as unknown as never)
  }
)

/** Debounce (timer simples) */
let t: number | undefined
const clearTimer = () => {
  if (t) {
    window.clearTimeout(t)
    t = undefined
  }
}
onBeforeUnmount(clearTimer)

/** Transforms normalize -> mask */
const applyTransforms = (raw: unknown): unknown => {
  const n = props.normalize ? props.normalize(raw) : raw
  const m = props.mask ? props.mask(n) : n
  return m
}

const immediateUpdate = (raw: unknown) => {
  const next = applyTransforms(raw)
  handleChange(next as never)
  emit('update:modelValue', next)
  emit('change', next)
}

const update = (raw: unknown) => {
  if (!props.debounce) return immediateUpdate(raw)
  clearTimer()
  t = window.setTimeout(() => {
    immediateUpdate(raw)
    t = undefined
  }, props.debounce) as unknown as number
}

/** Eventos comuns */
const onBlur = () => {
  setTouched(true)
  if (resolvedValidateOn.value === 'blur') handleChange(value.value as never)
  emit('blur')
}
const onFocus = () => emit('focus')
const onEnter = () => emit('enter')
const onClear = () => {
  update(undefined)
  emit('clear')
}

/** Bind padrão para inputs Vuetify */
const bind = computed(() => ({
  modelValue: value.value,
  'onUpdate:modelValue': update,
  error: !!errorMessage.value,
  errorMessages: errorMessage.value ? [errorMessage.value] : [],
  disabled: resolvedDisabled.value,
  readonly: resolvedReadonly.value,
  onFocus,
  onBlur,
  onKeyup: (e: KeyboardEvent) => {
    const key = (
      e.key ||
      (e as unknown as { code?: string }).code ||
      ''
    ).toLowerCase()
    if (key === 'enter') onEnter()
  },
}))

defineExpose({
  value,
  errorMessage,
  meta,
  update,
  setValue,
  setTouched,
  reset: resetField,
})
</script>

<template>
  <!-- Renderless: expõe API via slot -->
  <slot
    :bind="bind"
    :value="value"
    :error="errorMessage"
    :meta="meta"
    :disabled="props.disabled ?? formCtx?.disabled?.value ?? false"
    :readonly="props.readonly ?? formCtx?.readonly?.value ?? false"
    :on-clear="onClear"
  />
</template>
